import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Dashboard from "./pages/Dashboard";
import POProcessing from "./pages/POProcessing";
import Inventory from "./pages/Inventory";
import InventoryLegacy from "./pages/InventoryLegacy"; // Kept for backward compat, will not render
import OpsInventory from "./pages/OpsInventory";
import Packaging from "./pages/Packaging";
import InvoiceHistory from "./pages/InvoiceHistory";
import Analytics from "./pages/Analytics";
import SalesAnalytics from "./pages/SalesAnalytics";
import Settings from "./pages/Settings";
import DisplayKits from "./pages/DisplayKits";
import InventoryPlanning from "./pages/InventoryPlanning";
import RTODashboard from "./pages/RTODashboard";
import RepairsDashboard from "./pages/RepairsDashboard";
import UserManagement from "./pages/UserManagement";
import ShippingAutomation from "./pages/ShippingAutomation";
import DispatchCupboards from "./pages/DispatchCupboards";
import EmailPage from "./pages/EmailPage";
import ExportAssist from "./pages/ExportAssist";
import StoreDirectory from "./pages/StoreDirectory";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import { RefreshCw } from "lucide-react";

// AP Pages
import APInventory from "./pages/ap/APInventory";
import APPOProcessing from "./pages/ap/APPOProcessing";
import APInvoiceHistory from "./pages/ap/APInvoiceHistory";
import APSalesAnalytics from "./pages/ap/APSalesAnalytics";
import APStoreDirectory from "./pages/ap/APStoreDirectory";
import APEmailPage from "./pages/ap/APEmailPage";
import APAnalytics from "./pages/ap/APAnalytics";
import Viewfinder from "./pages/ap/Viewfinder";
import ViewfinderPublic from "./pages/ViewfinderPublic";

// EXTREME Pages
import EXPOProcessing from "./pages/extreme/EXPOProcessing";
import EXInventory from "./pages/extreme/EXInventory";
import EXInvoiceHistory from "./pages/extreme/EXInvoiceHistory";
import EXSalesAnalytics from "./pages/extreme/EXSalesAnalytics";
import EXStoreDirectory from "./pages/extreme/EXStoreDirectory";
import EXEmailPage from "./pages/extreme/EXEmailPage";
import EXAnalytics from "./pages/extreme/EXAnalytics";

// Wholesale Pages
import WholesaleDashboard from "./pages/wholesale/WholesaleDashboard";
import WholesaleOrders from "./pages/wholesale/WholesaleOrders";
import WholesaleProfile from "./pages/wholesale/WholesaleProfile";
import WholesaleAdmin from "./pages/wholesale/WholesaleAdmin";
import WholesaleAdminOrders from "./pages/wholesale/WholesaleAdminOrders";
import WholesaleAdminProducts from "./pages/wholesale/WholesaleAdminProducts";
import WholesaleAdminUsers from "./pages/wholesale/WholesaleAdminUsers";
import WholesaleAdminSettings from "./pages/wholesale/WholesaleAdminSettings";
import WholesaleAdminEditOrder from "./pages/wholesale/WholesaleAdminEditOrder";
import WholesaleConversations from "./pages/wholesale/WholesaleConversations";

function AppRoutes() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        {/* OBLIK */}
        <Route path="po" element={<POProcessing />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory-legacy" element={<InventoryLegacy />} />
        <Route path="invoices" element={<InvoiceHistory />} />
        <Route path="email" element={<EmailPage />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="sales-analytics" element={<SalesAnalytics />} />
        <Route path="store-directory" element={<StoreDirectory />} />
        {/* AP */}
        <Route path="ap/po" element={<APPOProcessing />} />
        <Route path="ap/inventory" element={<APInventory />} />
        <Route path="ap/invoices" element={<APInvoiceHistory />} />
        <Route path="ap/email" element={<APEmailPage />} />
        <Route path="ap/sales-analytics" element={<APSalesAnalytics />} />
        <Route path="ap/analytics" element={<APAnalytics />} />
        <Route path="ap/store-directory" element={<APStoreDirectory />} />
        <Route path="ap/viewfinder" element={<Viewfinder />} />
        {/* EXTREME */}
        <Route path="extreme/po" element={<EXPOProcessing />} />
        <Route path="extreme/inventory" element={<EXInventory />} />
        <Route path="extreme/invoices" element={<EXInvoiceHistory />} />
        <Route path="extreme/email" element={<EXEmailPage />} />
        <Route path="extreme/sales-analytics" element={<EXSalesAnalytics />} />
        <Route path="extreme/analytics" element={<EXAnalytics />} />
        <Route path="extreme/store-directory" element={<EXStoreDirectory />} />
        {/* Other */}
        <Route path="ops-inventory" element={<OpsInventory />} />
        <Route path="packaging" element={<Packaging />} />
        <Route path="display-kits" element={<DisplayKits />} />
        <Route path="inventory-planning" element={<InventoryPlanning />} />
        <Route path="rto" element={<RTODashboard />} />
        <Route path="repairs" element={<RepairsDashboard />} />
        <Route path="dispatch-cupboards" element={<DispatchCupboards />} />
        <Route path="shipping" element={<ShippingAutomation />} />
        <Route path="export-assist" element={<ExportAssist />} />
        <Route path="settings" element={<Settings />} />
        {/* Admin */}
        <Route path="users" element={isAdmin() ? <UserManagement /> : <Dashboard />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public viewfinder route - outside auth */}
            <Route path="/viewfinder/:token" element={<ViewfinderPublic />} />
            {/* Wholesale Portal routes - outside main layout */}
            <Route path="/wholesale" element={<WholesaleDashboard />} />
            <Route path="/wholesale/orders" element={<WholesaleOrders />} />
            <Route path="/wholesale/profile" element={<WholesaleProfile />} />
            <Route path="/wholesale/admin" element={<WholesaleAdmin />} />
            <Route path="/wholesale/admin/orders" element={<WholesaleAdminOrders />} />
            <Route path="/wholesale/admin/products" element={<WholesaleAdminProducts />} />
            <Route path="/wholesale/admin/users" element={<WholesaleAdminUsers />} />
            <Route path="/wholesale/admin/settings" element={<WholesaleAdminSettings />} />
            <Route path="/wholesale/admin/order/:oid/edit" element={<WholesaleAdminEditOrder />} />
            <Route path="/wholesale/admin/conversations" element={<WholesaleConversations />} />
            <Route path="/wholesale/conversations" element={<WholesaleConversations />} />
            {/* All other routes */}
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
